import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowRight, FileSpreadsheet, LibraryBig, Plus, ShieldCheck, Sparkles } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { FormField } from "@/components/FormField"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

type QueueItem = { id: number; reference: string; title: string; client_name: string }
type BoqSummary = {
  id: number
  reference: string
  title: string
  status: string
  source_type: string
  currency: string
  client_name: string
  item_count: number
  total_price: string
  total_cost?: string
  margin_percent?: string
  enquiry: { reference: string }
}

export default function BoqIndex({ boqs, estimating_queue, can_manage, can_view_margin, ai_import_enabled }: {
  boqs: BoqSummary[]
  estimating_queue: QueueItem[]
  can_manage: boolean
  can_view_margin: boolean
  ai_import_enabled: boolean
}) {
  const form = useForm({ enquiry_id: estimating_queue[0]?.id?.toString() || "", title: "" })

  return (
    <>
      <Head title="BOQs and quotations">
        <meta name="description" content="Build BOQs, protect internal margins and issue branded quotations." />
      </Head>
      <AppShell>
        <div className="flex flex-col gap-4 border-b border-hairline pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-accent-display">Commercial workspace</p>
            <h1 className="mt-2">BOQs & quotations</h1>
            <p className="mt-2 max-w-2xl text-sm text-ink-muted">Manual and spreadsheet-first estimating with protected costs, reviewable task codes and client-ready pricing.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">{can_view_margin && <Link href="/estimating/rates"><Button variant="secondary"><LibraryBig className="h-4 w-4" /> Rate library</Button></Link>}<div className="flex items-center gap-2 rounded-md border border-hairline bg-surface px-3 py-2 text-xs text-ink-muted">
            {ai_import_enabled ? <Sparkles className="h-4 w-4 text-accent-display" /> : <ShieldCheck className="h-4 w-4" />}
            Messy BOQ AI experiment {ai_import_enabled ? "enabled" : "off for the POC"}
          </div></div>
        </div>

        {can_manage && (
          <section className="mt-7 rounded-md border border-hairline bg-surface p-5">
            <h2 className="flex items-center gap-2 text-lg"><Plus className="h-5 w-5" /> Start from estimating queue</h2>
            {estimating_queue.length ? (
              <form className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end" onSubmit={(event) => {
                event.preventDefault()
                form.post("/estimating/boqs")
              }}>
                <FormField label="Enquiry" error={form.errors.enquiry_id}>
                  <Select className="mt-2" value={form.data.enquiry_id} onChange={(event) => form.setData("enquiry_id", event.target.value)}>
                    {estimating_queue.map((enquiry) => <option key={enquiry.id} value={enquiry.id}>{enquiry.reference} · {enquiry.client_name}</option>)}
                  </Select>
                </FormField>
                <FormField label="BOQ title" error={form.errors.title} hint="Leave blank to use the enquiry title.">
                  <Input className="mt-2" value={form.data.title} onChange={(event) => form.setData("title", event.target.value)} />
                </FormField>
                <Button type="submit" disabled={!form.data.enquiry_id || form.processing}>Create BOQ</Button>
              </form>
            ) : <p className="mt-3 text-sm text-ink-muted">No unassigned enquiries are waiting in estimating.</p>}
          </section>
        )}

        <section className="mt-8">
          <div className="flex items-center justify-between"><h2>Current BOQs</h2><span className="text-xs text-ink-muted">{boqs.length} records</span></div>
          <div className="mt-4 divide-y divide-hairline rounded-md border border-hairline">
            {boqs.map((boq) => (
              <Link key={boq.id} href={`/estimating/boqs/${boq.id}`} className="grid gap-3 p-4 no-underline hover:bg-surface sm:grid-cols-[1fr_auto] sm:items-center">
                <div className="flex min-w-0 gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-faded text-accent-display"><FileSpreadsheet className="h-5 w-5" /></span>
                  <div className="min-w-0"><p className="truncate font-medium text-ink-display">{boq.title}</p><p className="mt-1 text-xs text-ink-muted">{boq.reference} · {boq.client_name} · {boq.item_count} items · {boq.status}</p></div>
                </div>
                <div className="flex items-center justify-between gap-5 sm:justify-end">
                  <div className="text-right"><p className="font-medium text-ink-display">AED {Number(boq.total_price).toLocaleString()}</p>{boq.margin_percent !== undefined && <p className="text-xs text-ink-muted">{Number(boq.margin_percent).toFixed(1)}% margin</p>}</div>
                  <ArrowRight className="h-4 w-4 text-ink-muted" />
                </div>
              </Link>
            ))}
            {boqs.length === 0 && <p className="p-8 text-center text-sm text-ink-muted">No BOQs yet. Complete a site visit and hand it to estimating first.</p>}
          </div>
        </section>
      </AppShell>
    </>
  )
}
